/* eslint-disable @typescript-eslint/no-explicit-any */

'use client'

import { useDeviceType } from 'hooks/useDeviceType'
import { useRafLoop } from 'hooks/useRafLoop'
import { motion, PanInfo, useMotionValueEvent, useScroll, useSpring, useTransform } from 'motion/react'
import Image from 'next/image'
import { useRef } from 'react'
import { useWindowSize } from 'utils/hookHelpers'
import styles from './Marquee.module.scss'
import MarqueeItem from './MarqueeItem'

export interface MarqueeImage {
  src: string
  width: number
  height: number
  alt: string
}

type MarqueeProps = {
  images: MarqueeImage[]
  imagesTablet: MarqueeImage[]
  imagesMobile: MarqueeImage[]
  homepage?: boolean
}

const Marquee = ({ images, imagesTablet, imagesMobile, homepage = false }: MarqueeProps) => {
  const speed = 1.5,
    threshold = 0.014,
    wheelFactor = 1.5,
    dragFactor = 1.7
  const marqueeRef = useRef<HTMLDivElement>(null)
  const slowDown = useRef(false)
  const isScrolling = useRef<NodeJS.Timeout | null>(null)
  const { scrollY } = useScroll()
  const { isMobile, isTablet } = useDeviceType()

  const x = useRef(0)
  const { width } = useWindowSize()
  const speedSpring = useSpring(speed, {
    damping: 40,
    stiffness: 90,
    mass: 5,
  })

  const skewX = useTransform(speedSpring, [-width * 0.05, 0, width * 0.05], [1, 0, 1])

  const handleDragStart = () => {
    slowDown.current = true
    marqueeRef.current!.classList.add(styles.dragging)
    speedSpring.set(0)
  }

  const handleOnDrag = (_: any, info: PanInfo) => {
    speedSpring.set(dragFactor * -info.delta.x)
  }

  const handleDragEnd = () => {
    slowDown.current = false
    marqueeRef.current!.classList.remove(styles.dragging)
    //rest to the original speed
    x.current = speed
  }

  const loop = () => {
    /**
     * Do nothing if we're slowing down
     * or
     * Our x is less than the threshold
     * The threshold basically tells how much to speed up
     * Without this stop - x.current will mutiple expodentially
     */
    if (slowDown.current || Math.abs(x.current) < threshold) {
      return
    }

    /**
     * This portion speeds up the spring until it reaches the `threshold`
     */
    x.current *= 0.66

    if (x.current < 0) {
      x.current = Math.min(x.current, 0)
    } else {
      x.current = Math.max(x.current, 0)
    }

    //speedSpring sets the speed for the marquee items that gets passed to the item components
    speedSpring.set(speed + x.current)
  }

  useMotionValueEvent(scrollY, 'change', (current) => {
    const diff = current - scrollY.getPrevious()!
    x.current = diff * wheelFactor

    // reset speed on scroll end
    if (isScrolling.current) {
      window.clearTimeout(isScrolling.current)
    }

    isScrolling.current = setTimeout(() => {
      speedSpring.set(speed)
    }, 15)
  })

  useRafLoop(loop)

  return (
    <motion.div
      className={styles.marquee + (homepage ? ' ' + styles.homepage : '')}
      ref={marqueeRef}
      style={{ skewX }}
      drag='x'
      dragPropagation={true}
      dragConstraints={{ left: 0, right: 0 }}
      onDragStart={handleDragStart}
      onDrag={handleOnDrag}
      onDragEnd={handleDragEnd}
      dragElastic={0.000001} // needs to be > 0 ¯\_(ツ)_/¯
    >
      <MarqueeItem speed={speedSpring}>
        {isMobile
          ? imagesMobile.map((img) => <Image key={img.alt} alt={img.alt} src={img.src} width={img.width} height={img.height} draggable={false} />)
          : isTablet
            ? imagesTablet.map((img) => <Image key={img.alt} alt={img.alt} src={img.src} width={img.width} height={img.height} draggable={false} />)
            : images.map((img) => <Image key={img.alt} alt={img.alt} src={img.src} width={img.width} height={img.height} draggable={false} />)}
      </MarqueeItem>
      <MarqueeItem speed={speedSpring}>
        {isMobile
          ? imagesMobile.map((img) => <Image key={img.alt} alt={img.alt} src={img.src} width={img.width} height={img.height} draggable={false} />)
          : isTablet
            ? imagesTablet.map((img) => <Image key={img.alt} alt={img.alt} src={img.src} width={img.width} height={img.height} draggable={false} />)
            : images.map((img) => <Image key={img.alt} alt={img.alt} src={img.src} width={img.width} height={img.height} draggable={false} />)}
      </MarqueeItem>
    </motion.div>
  )
}

export default Marquee
